import { useMemo, useState } from "react";
import { Empty, Input, Popover, Tooltip, theme } from "antd";
import { TbCheck, TbChevronDown, TbSearch } from "react-icons/tb";
import {
  StageColor,
  StageIconTag,
  STAGE_COLOR_LIST,
  STAGE_ICON_LIST,
  resolveStageColor,
  resolveStageIcon,
} from "./constants";
import { IconBaseProps } from "react-icons";

interface TriggerProps {
  disabled?: boolean;
  open: boolean;
  children: React.ReactNode;
}

export const StageIcon = ({ iconTag, ...props }: { iconTag?: string } & IconBaseProps) => {
  const Icon = resolveStageIcon(iconTag);
  return <Icon {...props} />;
};

const PickerTrigger = ({ disabled, open, children }: TriggerProps) => {
  const { token } = theme.useToken();

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-expanded={open}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        height: 40,
        padding: "0 11px",
        borderRadius: token.borderRadius,
        border: `1px solid ${open ? token.colorPrimary : token.colorBorder}`,
        boxShadow: open ? `0 0 0 2px ${token.controlOutline}` : "none",
        background: disabled ? token.colorBgContainerDisabled : token.colorBgContainer,
        color: disabled ? token.colorTextDisabled : token.colorText,
        cursor: disabled ? "not-allowed" : "pointer",
        transitionProperty: "border-color, box-shadow",
        transitionDuration: "0.2s",
        transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
        userSelect: "none",
      }}
    >
      {children}
      <TbChevronDown
        size={16}
        style={{
          color: token.colorTextQuaternary,
          flexShrink: 0,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transitionProperty: "transform",
          transitionDuration: "0.2s",
          transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
        }}
      />
    </div>
  );
};

interface StageIconPickerProps {
  value?: StageIconTag;
  onChange?: (value: StageIconTag) => void;
  disabled?: boolean;
  color?: StageColor | string;
}

export const StageIconPicker = ({ value, onChange, disabled, color }: StageIconPickerProps) => {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const swatch = resolveStageColor(color);
  const Selected = resolveStageIcon(value);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return STAGE_ICON_LIST;
    return STAGE_ICON_LIST.filter((entry) => entry.label.toLowerCase().includes(needle));
  }, [query]);

  const content = (
    <div style={{ width: 292 }}>
      <Input
        allowClear
        autoFocus
        placeholder="Search icons"
        prefix={<TbSearch size={15} color={token.colorTextQuaternary} />}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        style={{ marginBottom: 10 }}
      />
      {results.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No icon matches that name"
          style={{ margin: "12px 0" }}
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 4,
            maxHeight: 232,
            overflowY: "auto",
          }}
        >
          {results.map(({ value: iconValue, label, Icon }) => {
            const active = iconValue === value;
            return (
              <Tooltip key={iconValue} title={label} mouseEnterDelay={0.4}>
                <button
                  type="button"
                  aria-label={label}
                  aria-pressed={active}
                  onClick={() => {
                    onChange?.(iconValue);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 44,
                    height: 40,
                    border: `1px solid ${active ? swatch.border : "transparent"}`,
                    borderRadius: token.borderRadius,
                    background: active ? swatch.bg : "transparent",
                    color: active ? swatch.text : token.colorTextSecondary,
                    cursor: "pointer",
                    transitionProperty: "background-color, color",
                    transitionDuration: "0.15s",
                  }}
                >
                  <Icon size={19} />
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      destroyTooltipOnHide
    >
      <div>
        <PickerTrigger disabled={disabled} open={open}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                borderRadius: 7,
                background: swatch.bg,
                color: swatch.text,
                flexShrink: 0,
              }}
            >
              <Selected size={16} />
            </span>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value ? STAGE_ICON_LIST.find((entry) => entry.value === value)?.label : "Choose an icon"}
            </span>
          </span>
        </PickerTrigger>
      </div>
    </Popover>
  );
};

interface StageColorPickerProps {
  value?: StageColor;
  onChange?: (value: StageColor) => void;
  disabled?: boolean;
}

export const StageColorPicker = ({ value, onChange, disabled }: StageColorPickerProps) => {
  const { token } = theme.useToken();

  return (
    <div
      role="radiogroup"
      aria-label="Stage colour"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(8, 1fr)",
        gap: 2,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {STAGE_COLOR_LIST.map(({ value: colorValue, token: swatch }) => {
        const active = colorValue === value;
        return (
          <Tooltip key={colorValue} title={swatch.label} mouseEnterDelay={0.3}>
            <button
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={swatch.label}
              onClick={() => onChange?.(colorValue)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                border: "none",
                background: "transparent",
                borderRadius: token.borderRadius,
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: swatch.solid,
                  color: "#fff",
                  boxShadow: active
                    ? `0 0 0 2px ${token.colorBgContainer}, 0 0 0 4px ${swatch.solid}`
                    : "inset 0 0 0 1px rgba(0,0,0,0.06)",
                  transitionProperty: "box-shadow, transform",
                  transitionDuration: "0.15s",
                  transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
                  transform: active ? "scale(1)" : "scale(0.92)",
                }}
              >
                {active ? <TbCheck size={14} /> : null}
              </span>
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
